import { defaultTipOptions, migrateTipContent } from '@/utils/tipConfig'

/** Section content values are mostly strings; `tipOptions` is stored as a JSON array. */
export type SectionContent = Record<string, unknown>

export function sectionContentText(
  content: SectionContent,
  key: string,
  fallback = '',
): string {
  const value = content[key]
  return typeof value === 'string' ? value : fallback
}

/** True when Quill/HTML has visible text or media (ignores empty `<p><br></p>`). */
export function hasRichHtmlContent(html: string): boolean {
  const raw = html.trim()
  if (!raw) return false
  if (/<(img|video|iframe|svg)\b/i.test(raw)) return true
  const text = raw
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim()
  return text.length > 0
}

export type CheckoutSectionId =
  | 'orderSummaryMobileHeader'
  | 'checkoutHeader'
  | 'countdown'
  | 'expressCheckout'
  | 'contact'
  | 'delivery'
  | 'shippingMethod'
  | 'tip'
  | 'payment'
  | 'paymentCreditCard'
  | 'paymentBilling'
  | 'payButton'
  | 'paymentRecurring'
  | 'discountCode'
  | 'orderSummary'
  | 'checkoutFooter'

export type SectionContentFieldControl =
  | 'text'
  | 'textarea'
  | 'select'
  | 'switch'
  | 'color'
  | 'richtext'

export type SectionContentFieldOption = {
  value: string
  label: string
}

export type SectionContentField = {
  key: string
  label: string
  group?: string
  multiline?: boolean
  placeholder?: string
  /** Default text; switch uses '1' / '0' */
  control?: SectionContentFieldControl
  options?: SectionContentFieldOption[]
}

/** Page-level basic settings (document title, etc.) edited in the "Page Basic Config" panel. */
export type PageBaseFieldControl = 'text' | 'textarea' | 'image' | 'size'

export type PageBaseField = {
  key: string
  label: string
  group?: string
  multiline?: boolean
  placeholder?: string
  control?: PageBaseFieldControl
}

export const DEFAULT_PAGE_META: Record<string, string> = {
  pageTitle: 'Secure Checkout',
  faviconUrl: '',
  logoUrl: '',
  // '' / 'auto' → 跟随样式默认尺寸；'<n>px' → 固定宽/高。
  logoWidth: 'auto',
  logoHeight: 'auto',
}

export const PAGE_BASE_FIELDS: PageBaseField[] = [
  { key: 'pageTitle', label: 'Page Title', group: 'Basic', placeholder: 'Secure Checkout' },
  { key: 'faviconUrl', label: 'Favicon URL', group: 'Basic', placeholder: 'https://...' },
  { key: 'logoUrl', label: 'Logo', group: 'Logo', control: 'image' },
  { key: 'logoWidth', label: 'Width', group: 'Logo', control: 'size' },
  { key: 'logoHeight', label: 'Height', group: 'Logo', control: 'size' },
]

export function mergePageMeta(meta?: Record<string, string>): Record<string, string> {
  return { ...DEFAULT_PAGE_META, ...(meta ?? {}) }
}

/** Express checkout wallet layout (yx-payment-sdk) */
export type ExpressWalletLayoutMode = 'stack' | 'inline' | 'grid' | 'row3' | 'grid3'

export type ExpressWalletViewportMode = 'auto' | 'desktop' | 'mobile'

export const EXPRESS_WALLET_LAYOUT_OPTIONS: SectionContentFieldOption[] = [
  { value: 'row3', label: 'Single row, 3 columns (row3)' },
  { value: 'grid', label: 'Two-column grid (grid)' },
  { value: 'grid3', label: 'Three equal columns (grid3)' },
  { value: 'inline', label: 'Inline, wraps (inline)' },
  { value: 'stack', label: 'Vertical stack (stack)' },
]

export const EXPRESS_WALLET_VIEWPORT_OPTIONS: SectionContentFieldOption[] = [
  { value: 'auto', label: 'Auto (follow container width)' },
  { value: 'desktop', label: 'Desktop (force multi-column)' },
  { value: 'mobile', label: 'Mobile (force single column)' },
]

const EXPRESS_WALLET_LAYOUT_SET = new Set<string>(
  EXPRESS_WALLET_LAYOUT_OPTIONS.map((o) => o.value),
)

const EXPRESS_WALLET_VIEWPORT_SET = new Set<string>(
  EXPRESS_WALLET_VIEWPORT_OPTIONS.map((o) => o.value),
)

export function sectionContentEnabled(
  content: SectionContent,
  key: string,
  defaultEnabled = true,
): boolean {
  const raw = String(content[key] ?? '').trim().toLowerCase()
  if (!raw) return defaultEnabled
  return raw === '1' || raw === 'true' || raw === 'yes' || raw === 'on'
}

export function parseExpressWalletLayout(
  value: string | undefined,
  fallback: ExpressWalletLayoutMode = 'row3',
): ExpressWalletLayoutMode {
  const v = String(value ?? '').trim()
  return EXPRESS_WALLET_LAYOUT_SET.has(v) ? (v as ExpressWalletLayoutMode) : fallback
}

export function parseExpressWalletViewport(
  value: string | undefined,
  fallback: ExpressWalletViewportMode = 'auto',
): ExpressWalletViewportMode {
  const v = String(value ?? '').trim()
  return EXPRESS_WALLET_VIEWPORT_SET.has(v)
    ? (v as ExpressWalletViewportMode)
    : fallback
}

export type SectionListMeta = {
  /** Label in editor sidebar */
  listLabel: string
  /** Indent under parent in sidebar */
  indent?: boolean
  /** Parent section for visibility cascade */
  parentId?: CheckoutSectionId
}

/** Layout editor / preview scroll: child rows focus the parent block in checkout. */
export function sectionPreviewScrollId(id: CheckoutSectionId): CheckoutSectionId {
  return SECTION_LIST_META[id]?.parentId ?? id
}

export type AddressFormFieldOptions = {
  showFirstName: boolean
  showLastName: boolean
  showCompany: boolean
  showApartment: boolean
  firstNameRequired: boolean
  lastNameRequired: boolean
  companyRequired: boolean
}

export function resolveAddressFormFieldOptions(
  content: SectionContent,
  overrides: Partial<AddressFormFieldOptions> = {},
): AddressFormFieldOptions {
  return {
    showFirstName: sectionContentEnabled(content, 'showFirstName', true),
    showLastName: sectionContentEnabled(content, 'showLastName', true),
    showCompany: sectionContentEnabled(content, 'showCompany', false),
    showApartment: sectionContentEnabled(content, 'showApartment', true),
    firstNameRequired: sectionContentEnabled(content, 'firstNameRequired', true),
    lastNameRequired: sectionContentEnabled(content, 'lastNameRequired', true),
    companyRequired: sectionContentEnabled(content, 'companyRequired', false),
    ...overrides,
  }
}

/** Checkout footer policy links — API type + display config */
export type CheckoutFooterPolicyType =
  | 'privacyPolicy'
  | 'refundPolicy'
  | 'shippingPolicy'
  | 'subscriptionPolicy'
  | 'termsOfService'

export type CheckoutFooterPolicyLink = {
  type: CheckoutFooterPolicyType
  label: string
}

const CHECKOUT_FOOTER_POLICY_DEFS: Array<{
  type: CheckoutFooterPolicyType
  showKey: string
  labelKey: string
  defaultLabel: string
}> = [
  {
    type: 'refundPolicy',
    showKey: 'showRefundPolicy',
    labelKey: 'refundLabel',
    defaultLabel: 'Refund policy',
  },
  {
    type: 'shippingPolicy',
    showKey: 'showShippingPolicy',
    labelKey: 'shippingLabel',
    defaultLabel: 'Shipping',
  },
  {
    type: 'privacyPolicy',
    showKey: 'showPrivacyPolicy',
    labelKey: 'privacyLabel',
    defaultLabel: 'Privacy policy',
  },
  {
    type: 'termsOfService',
    showKey: 'showTermsOfService',
    labelKey: 'termsOfServiceLabel',
    defaultLabel: 'Terms of service',
  },
  {
    type: 'subscriptionPolicy',
    showKey: 'showSubscriptionPolicy',
    labelKey: 'subscriptionLabel',
    defaultLabel: 'Cancellations',
  },
]

export function resolveCheckoutFooterPolicies(
  content: SectionContent,
): CheckoutFooterPolicyLink[] {
  return CHECKOUT_FOOTER_POLICY_DEFS.filter((def) =>
    sectionContentEnabled(content, def.showKey, true),
  ).map((def) => ({
    type: def.type,
    label: sectionContentText(content, def.labelKey, def.defaultLabel) || def.defaultLabel,
  }))
}

export type CheckoutLayoutSection = {
  id: CheckoutSectionId
  label: string
  visible: boolean
  content?: SectionContent
}

export type CheckoutLayoutConfig = {
  sections: CheckoutLayoutSection[]
}

export const CHECKOUT_LAYOUT_STORAGE_KEY = 'checkout-layout-config'

/** Editor sidebar rows that cannot be drag-reordered (config-only blocks). */
export const NON_REORDERABLE_SECTION_IDS: CheckoutSectionId[] = ['discountCode']

/** Main left-column blocks on checkout page */
export const LEFT_COLUMN_SECTION_IDS: CheckoutSectionId[] = [
  'checkoutHeader',
  'countdown',
  'expressCheckout',
  'contact',
  'delivery',
  'shippingMethod',
  'tip',
  'payment',
]

/** Checkout funnel layout type (mirrors the admin funnel `templateType`). */
export type CheckoutFunnelType = 'one_page_checkout' | 'three_pages_checkout'

const FUNNEL_TYPE_SET = new Set<string>([
  'one_page_checkout',
  'three_pages_checkout',
])

export function normalizeFunnelType(value: unknown): CheckoutFunnelType {
  const v = String(value ?? '').trim()
  return FUNNEL_TYPE_SET.has(v) ? (v as CheckoutFunnelType) : 'one_page_checkout'
}

/** Sub-parts inside Payment (editor only; rendered inside PaymentSection) */
export const PAYMENT_CHILD_SECTION_IDS: CheckoutSectionId[] = [
  'paymentCreditCard',
  'paymentBilling',
  'payButton',
  'paymentRecurring',
]

export const SECTION_LIST_META: Record<CheckoutSectionId, SectionListMeta> = {
  orderSummaryMobileHeader: { listLabel: 'Mobile Order Summary' },
  checkoutHeader: { listLabel: 'Checkout Header' },
  countdown: { listLabel: 'Countdown' },
  expressCheckout: { listLabel: 'Express Checkout' },
  contact: { listLabel: 'Contact' },
  delivery: { listLabel: 'Delivery' },
  shippingMethod: { listLabel: 'Shipping Method' },
  tip: { listLabel: 'Tip' },
  payment: { listLabel: 'Payment' },
  paymentCreditCard: {
    listLabel: 'Credit Card Form',
    indent: true,
    parentId: 'payment',
  },
  paymentBilling: {
    listLabel: 'PayPal Billing Address',
    indent: true,
    parentId: 'payment',
  },
  payButton: {
    listLabel: 'Pay Button',
    indent: true,
    parentId: 'payment',
  },
  paymentRecurring: {
    listLabel: 'Subscription / Recurring Notice',
    indent: true,
    parentId: 'payment',
  },
  discountCode: { listLabel: 'Discount Code' },
  orderSummary: { listLabel: 'Order Summary' },
  checkoutFooter: { listLabel: 'Checkout Footer' },
}

export const DEFAULT_SECTION_CONTENT: Record<CheckoutSectionId, SectionContent> = {
  orderSummaryMobileHeader: {
    collapseLabel: 'Order summary',
  },
  checkoutHeader: {
    richHtml: '',
  },
  countdown: {
    countDownStyle: 'banner',
    countDownText: 'Your order is reserved for ',
    countDownColor: '#000000',
    countDownFontSize: '14',
    countDownDuration: '600',
    countDownDurationColor: '#000000',
    countDownDurationFontSize: '14',
    countDownBorder: '#100000',
    countDownBg: '#f6f6f6',
    countDownBottomGap: '30',
  },
  expressCheckout: {
    title: 'Express checkout',
    orDividerText: 'OR',
    configHint: 'PayPal Client ID is not configured. Wallet buttons cannot load.',
    walletLayout: 'row3',
    walletViewport: 'auto',
    showPaypal: '1',
    showGooglePay: '1',
    showApplePay: '1',
  },
  contact: {
    title: 'Contact',
    emailLabel: 'Email',
    showEmailMarketing: '1',
    emailMarketingLabel: 'Email me with news and offers',
  },
  delivery: {
    title: 'Delivery',
    phoneLabel: 'Phone',
    showSmsMarketing: '0',
    smsMarketingLabel: 'Text me with news and offers',
    showFirstName: '1',
    showLastName: '1',
    showCompany: '0',
    showApartment: '1',
    firstNameRequired: '1',
    lastNameRequired: '1',
    companyRequired: '0',
    countryLabel: 'Country/Region',
    firstNameLabel: 'First name',
    lastNameLabel: 'Last name',
    companyLabel: 'Company',
    addressLabel: 'Address',
    apartmentLabel: 'Apartment, suite, etc. (optional)',
    cityLabel: 'City',
  },
  shippingMethod: {
    title: 'Shipping method',
    shippingSource: 'shop',
    emptyText: 'Enter your shipping address to view available shipping methods.',
    customEmptyText: 'Please add at least one shipping method.',
    customShippingMethods:
      '[{"id":"standard","name":"Standard shipping","description":"5–7 business days","price":5,"defaultChecked":true},{"id":"express","name":"Express shipping","description":"2–3 business days","price":15,"defaultChecked":false}]',
  },
  tip: {
    title: 'Add a tip',
    supportMessage: 'Show your support for the team',
    thankYouMessage: 'Thank you, we appreciate it.',
    customPlaceholder: 'Custom tip',
    addTipLabel: 'Add tip',
    noneLabel: 'No tip',
    summaryLabel: 'Tip',
    showToggle: true,
    defaultTipEnabled: true,
    showNoneOption: true,
    tipOptions: defaultTipOptions(),
    defaultOptionId: 'tip-p1',
  },
  payment: {
    title: 'Payment',
    hint: 'All transactions are secure and encrypted.',
    paypalRedirectText: "You'll be redirected to PayPal to complete your purchase",
  },
  paymentCreditCard: {
    cardNumberLabel: 'Card number',
    expiryLabel: 'Expiration date (MM / YY)',
    cvcLabel: 'Security code',
    cvcHelpText:
      '3-digit security code usually found on the back of your card. American Express cards have a 4-digit code located on the front.',
    nameLabel: 'Name on card',
    useShippingAsBillingLabel: 'Use shipping address as billing address',
    billingAddressTitle: 'Billing address',
  },
  paymentBilling: {
    billingTitle: 'Billing address',
    billingSameLabel: 'Same as shipping address',
    billingDifferentLabel: 'Use a different billing address',
    showFirstName: '1',
    showLastName: '1',
    showCompany: '0',
    showApartment: '1',
    firstNameRequired: '0',
    lastNameRequired: '1',
    companyRequired: '0',
    countryLabel: 'Country/Region',
    firstNameLabel: 'First name',
    lastNameLabel: 'Last name',
    companyLabel: 'Company',
    addressLabel: 'Address',
    apartmentLabel: 'Apartment, suite, etc. (optional)',
    cityLabel: 'City',
  },
  payButton: {
    payNowLabel: 'Pay now',
    payingAriaLabel: 'Processing payment',
  },
  paymentRecurring: {
    recurringMultipleText: 'This order has a recurring charge for multiple items.',
    recurringHelpText:
      'Does not include shipping, tax, duties, or any applicable discounts.',
    subscriptionLegalPrefix:
      'One or more items in your cart is a deferred, subscription, or recurring purchase. By continuing with your payment, you agree that your payment method will automatically be charged at the price and frequency listed on this page until it ends or you cancel. All cancellations are subject to the',
    cancellationPolicyLinkText: 'Cancellation policy',
  },
  discountCode: {
    display: 'orderSummary',
    text: 'Discount code',
  },
  orderSummary: {
    subtotalLabel: 'Subtotal',
    shippingLabel: 'Shipping',
    totalLabel: 'Total',
    enterShippingLabel: 'Enter shipping address',
    freeShippingLabel: 'Free',
    recurringSubtotalLabel: 'Recurring subtotal',
    recurringHelpText:
      'Does not include shipping, tax, duties, or any applicable discounts.',
    showTotalSavings: '1',
    showOriginalPrice: '1',
    totalSavingsLabel: 'TOTAL SAVINGS',
    isTestimonialsFirst: '1',
    testimonialsEnabled: '0',
    testimonialsTitle: 'Why choose us?',
    testimonialsTitleFontSize: '14',
    testimonials: '[]',
    customerReviewsEnabled: '0',
    customerReviewsTitle: 'Customer Reviews',
    customerReviewsTitleFontSize: '14',
    customerReviews: '[]',
  },
  checkoutFooter: {
    richHtml: '',
    policyLinkFontSize: '13',
    policyLinkColor: '#333333',
    showRefundPolicy: '1',
    refundLabel: 'Refund policy',
    showShippingPolicy: '1',
    shippingLabel: 'Shipping',
    showPrivacyPolicy: '1',
    privacyLabel: 'Privacy policy',
    showTermsOfService: '1',
    termsOfServiceLabel: 'Terms of service',
    showSubscriptionPolicy: '1',
    subscriptionLabel: 'Cancellations',
  },
}

export const SECTION_CONTENT_FIELDS: Record<CheckoutSectionId, SectionContentField[]> = {
  orderSummaryMobileHeader: [{ key: 'collapseLabel', label: 'Collapse Bar Title' }],
  checkoutHeader: [
    {
      key: 'richHtml',
      label: 'Content',
      group: 'Content',
      control: 'richtext',
      placeholder: 'Add header text, images, etc.',
    },
  ],
  countdown: [
    {
      key: 'countDownStyle',
      label: 'Style',
      group: 'Style',
      control: 'select',
      options: [
        { value: 'banner', label: 'Banner (classic notice)' },
        { value: 'digital', label: 'Digital clock' },
        { value: 'blocks', label: 'Flip blocks' },
        { value: 'pill', label: 'Compact pill' },
        { value: 'minimal', label: 'Minimal underline' },
        { value: 'editorial', label: 'Editorial (elegant type)' },
        { value: 'split', label: 'Split bar' },
        { value: 'soft', label: 'Soft card' },
        { value: 'display', label: 'Display (large type)' },
      ],
    },
    {
      key: 'countDownBottomGap',
      label: 'Bottom Spacing (px)',
      group: 'Spacing',
      placeholder: '30',
    },
    {
      key: 'countDownText',
      label: 'Description Text',
      group: 'Description',
      placeholder: 'Your order is reserved for ',
    },
    {
      key: 'countDownColor',
      label: 'Description Color',
      group: 'Description',
      control: 'color',
      placeholder: '#000000',
    },
    {
      key: 'countDownFontSize',
      label: 'Description Font Size (px)',
      group: 'Description',
      placeholder: '14',
    },
    {
      key: 'countDownDuration',
      label: 'Duration (seconds)',
      group: 'Timer',
      placeholder: '600',
    },
    {
      key: 'countDownDurationColor',
      label: 'Timer Color',
      group: 'Timer',
      control: 'color',
      placeholder: '#000000',
    },
    {
      key: 'countDownDurationFontSize',
      label: 'Timer Font Size (px)',
      group: 'Timer',
      placeholder: '14',
    },
    {
      key: 'countDownBg',
      label: 'Background Color',
      group: 'Background',
      control: 'color',
      placeholder: '#f6f6f6',
    },
    {
      key: 'countDownBorder',
      label: 'Border Color',
      group: 'Background',
      control: 'color',
      placeholder: '#100000',
    },
  ],
  expressCheckout: [
    { key: 'title', label: 'Section Title', group: 'Basic' },
    { key: 'orDividerText', label: 'Divider Text (OR)', group: 'Basic' },
    {
      key: 'configHint',
      label: 'Missing Client ID Hint',
      group: 'Basic',
      multiline: true,
    },
    {
      key: 'walletLayout',
      label: 'Button Layout (Desktop)',
      group: 'Button Layout (Desktop)',
      control: 'select',
      options: EXPRESS_WALLET_LAYOUT_OPTIONS,
    },
    {
      key: 'walletViewport',
      label: 'Viewport Mode',
      group: 'Viewport Mode',
      control: 'select',
      options: EXPRESS_WALLET_VIEWPORT_OPTIONS,
    },
    {
      key: 'showPaypal',
      label: 'Show PayPal',
      group: 'Button Visibility',
      control: 'switch',
    },
    {
      key: 'showGooglePay',
      label: 'Show Google Pay',
      group: 'Button Visibility',
      control: 'switch',
    },
    {
      key: 'showApplePay',
      label: 'Show Apple Pay',
      group: 'Button Visibility',
      control: 'switch',
    },
  ],
  contact: [
    { key: 'title', label: 'Section Title', group: 'Basic' },
    { key: 'showEmailMarketing', label: 'Show Email Marketing Checkbox', group: 'Form Fields', control: 'switch' },
    { key: 'emailLabel', label: 'Email Field Label', group: 'Form Labels' },
    { key: 'emailMarketingLabel', label: 'Email Marketing Checkbox Text', group: 'Form Labels' },
  ],
  delivery: [
    { key: 'title', label: 'Section Title', group: 'Basic' },
    { key: 'showSmsMarketing', label: 'Show SMS Marketing Checkbox', group: 'Form Fields', control: 'switch' },
    { key: 'showFirstName', label: 'Show First Name', group: 'Form Fields', control: 'switch' },
    { key: 'showLastName', label: 'Show Last Name', group: 'Form Fields', control: 'switch' },
    { key: 'showCompany', label: 'Show Company', group: 'Form Fields', control: 'switch' },
    { key: 'showApartment', label: 'Show Apartment/Suite', group: 'Form Fields', control: 'switch' },
    {
      key: 'firstNameRequired',
      label: 'First Name Required',
      group: 'Form Validation',
      control: 'switch',
    },
    {
      key: 'lastNameRequired',
      label: 'Last Name Required',
      group: 'Form Validation',
      control: 'switch',
    },
    {
      key: 'companyRequired',
      label: 'Company Required',
      group: 'Form Validation',
      control: 'switch',
    },
    { key: 'countryLabel', label: 'Country/Region', group: 'Form Labels' },
    { key: 'firstNameLabel', label: 'First Name', group: 'Form Labels' },
    { key: 'lastNameLabel', label: 'Last Name', group: 'Form Labels' },
    { key: 'companyLabel', label: 'Company', group: 'Form Labels' },
    { key: 'addressLabel', label: 'Address (search placeholder)', group: 'Form Labels' },
    { key: 'apartmentLabel', label: 'Apartment/Suite', group: 'Form Labels' },
    { key: 'cityLabel', label: 'City', group: 'Form Labels' },
    { key: 'phoneLabel', label: 'Phone Field Label', group: 'Form Labels' },
    { key: 'smsMarketingLabel', label: 'SMS Marketing Checkbox Text', group: 'Form Labels' },
  ],
  shippingMethod: [
    { key: 'title', label: 'Section Title', group: 'Basic' },
    {
      key: 'shippingSource',
      label: 'Shipping Method Source',
      group: 'Shipping Source',
      control: 'select',
      options: [
        { value: 'shop', label: 'Store shipping methods (default)' },
        { value: 'custom', label: 'Custom shipping methods' },
      ],
    },
    {
      key: 'customEmptyText',
      label: 'Custom List Empty Hint',
      group: 'Empty State',
      multiline: true,
    },
    {
      key: 'emptyText',
      label: 'Store mode · No address hint',
      group: 'Empty State',
      multiline: true,
    },
  ],
  tip: [
    { key: 'title', label: 'Section Title', group: 'Basic' },
    {
      key: 'showToggle',
      label: 'Show "Add a tip" checkbox',
      group: 'Add Tip Toggle',
      control: 'switch',
    },
    {
      key: 'defaultTipEnabled',
      label: 'Add tip checked by default',
      group: 'Add Tip Toggle',
      control: 'switch',
    },
    { key: 'supportMessage', label: 'Checkbox Description', group: 'Add Tip Toggle', multiline: true },
    { key: 'showNoneOption', label: 'Show "No tip" button', group: 'Quick Percentages', control: 'switch' },
    { key: 'noneLabel', label: 'No Tip Button Text', group: 'Quick Percentages' },
    { key: 'customPlaceholder', label: 'Custom Amount Placeholder', group: 'Custom' },
    { key: 'addTipLabel', label: 'Add Button Text', group: 'Custom' },
    { key: 'thankYouMessage', label: 'Thank You Message', group: 'Custom', multiline: true },
    { key: 'summaryLabel', label: 'Order Summary Row Title', group: 'Order Summary' },
  ],
  payment: [
    { key: 'title', label: 'Section Title', group: 'Basic' },
    { key: 'hint', label: 'Security Note', group: 'Basic', multiline: true },
    {
      key: 'paypalRedirectText',
      label: 'PayPal Redirect Hint',
      group: 'PayPal',
      multiline: true,
    },
  ],
  paymentCreditCard: [
    { key: 'cardNumberLabel', label: 'Card Number', group: 'Form Fields' },
    { key: 'expiryLabel', label: 'Expiration Date', group: 'Form Fields' },
    { key: 'cvcLabel', label: 'Security Code', group: 'Form Fields' },
    { key: 'cvcHelpText', label: 'Security Code Help', group: 'Form Fields', multiline: true },
    { key: 'nameLabel', label: 'Name on Card', group: 'Form Fields' },
    {
      key: 'useShippingAsBillingLabel',
      label: 'Billing Address Checkbox Text',
      group: 'Billing Address',
    },
    { key: 'billingAddressTitle', label: 'Billing Address Subtitle', group: 'Billing Address' },
  ],
  paymentBilling: [
    { key: 'billingTitle', label: 'Billing Address Title', group: 'Basic' },
    { key: 'billingSameLabel', label: 'Same as Shipping Option', group: 'Option Labels' },
    { key: 'billingDifferentLabel', label: 'Separate Billing Option', group: 'Option Labels' },
    { key: 'showFirstName', label: 'Show First Name', group: 'Form Fields', control: 'switch' },
    { key: 'showLastName', label: 'Show Last Name', group: 'Form Fields', control: 'switch' },
    { key: 'showCompany', label: 'Show Company', group: 'Form Fields', control: 'switch' },
    { key: 'showApartment', label: 'Show Apartment/Suite', group: 'Form Fields', control: 'switch' },
    {
      key: 'firstNameRequired',
      label: 'First Name Required',
      group: 'Form Validation',
      control: 'switch',
    },
    {
      key: 'lastNameRequired',
      label: 'Last Name Required',
      group: 'Form Validation',
      control: 'switch',
    },
    {
      key: 'companyRequired',
      label: 'Company Required',
      group: 'Form Validation',
      control: 'switch',
    },
    { key: 'countryLabel', label: 'Country/Region', group: 'Form Labels' },
    { key: 'firstNameLabel', label: 'First Name', group: 'Form Labels' },
    { key: 'lastNameLabel', label: 'Last Name', group: 'Form Labels' },
    { key: 'companyLabel', label: 'Company', group: 'Form Labels' },
    { key: 'addressLabel', label: 'Address', group: 'Form Labels' },
    { key: 'apartmentLabel', label: 'Apartment/Suite', group: 'Form Labels' },
    { key: 'cityLabel', label: 'City', group: 'Form Labels' },
  ],
  payButton: [
    { key: 'payNowLabel', label: 'Button Text', group: 'Pay Button' },
    { key: 'payingAriaLabel', label: 'Processing Aria Label', group: 'Pay Button' },
  ],
  paymentRecurring: [
    {
      key: 'recurringMultipleText',
      label: 'Multiple Items Subscription Note',
      group: 'Recurring',
      multiline: true,
    },
    {
      key: 'recurringHelpText',
      label: 'Recurring Charge Help',
      group: 'Recurring',
      multiline: true,
    },
    {
      key: 'subscriptionLegalPrefix',
      label: 'Subscription Legal Note (prefix)',
      group: 'Subscription Notice',
      multiline: true,
    },
    {
      key: 'cancellationPolicyLinkText',
      label: 'Cancellation Policy Link Text',
      group: 'Subscription Notice',
    },
  ],
  discountCode: [
    {
      key: 'display',
      label: 'Display Position',
      group: 'Placement',
      control: 'select',
      options: [
        { value: 'orderSummary', label: 'Order Summary' },
        { value: 'checkoutTop', label: 'Checkout Top' },
        { value: 'checkoutBottom', label: 'Checkout Bottom' },
      ],
    },
    {
      key: 'text',
      label: 'Placeholder',
      group: 'Content',
      placeholder: 'Discount code',
    },
  ],
  orderSummary: [
    { key: 'subtotalLabel', label: 'Subtotal Row Title', group: 'Summary' },
    { key: 'shippingLabel', label: 'Shipping Row Title', group: 'Summary' },
    { key: 'totalLabel', label: 'Total Row Title', group: 'Summary' },
    { key: 'enterShippingLabel', label: 'No Address Shipping Hint', group: 'Summary' },
    { key: 'freeShippingLabel', label: 'Free Shipping Text', group: 'Summary' },
    { key: 'recurringSubtotalLabel', label: 'Recurring Subtotal Title', group: 'Summary' },
    { key: 'recurringHelpText', label: 'Recurring Subtotal Help', group: 'Summary', multiline: true },
    { key: 'showOriginalPrice', label: 'Show Original Price (strikethrough)', group: 'Display', control: 'switch' },
    { key: 'showTotalSavings', label: 'Show Total Savings', group: 'Display', control: 'switch' },
    { key: 'totalSavingsLabel', label: 'Total Savings Text', group: 'Display' },
    {
      key: 'isTestimonialsFirst',
      label: 'Show “Why choose us?” before Customer Reviews',
      group: 'Below Summary',
      control: 'switch',
    },
    {
      key: 'testimonialsEnabled',
      label: 'Show Why choose us?',
      group: 'Why choose us?',
      control: 'switch',
    },
    { key: 'testimonialsTitle', label: 'Section Title', group: 'Why choose us?' },
    {
      key: 'testimonialsTitleFontSize',
      label: 'Title Font Size (px)',
      group: 'Why choose us?',
      placeholder: '14',
    },
    {
      key: 'testimonials',
      label: 'Items',
      group: 'Why choose us?',
    },
    {
      key: 'customerReviewsEnabled',
      label: 'Show Customer Reviews',
      group: 'Customer Reviews',
      control: 'switch',
    },
    { key: 'customerReviewsTitle', label: 'Section Title', group: 'Customer Reviews' },
    {
      key: 'customerReviewsTitleFontSize',
      label: 'Title Font Size (px)',
      group: 'Customer Reviews',
      placeholder: '14',
    },
    {
      key: 'customerReviews',
      label: 'Items',
      group: 'Customer Reviews',
    },
  ],
  checkoutFooter: [
    {
      key: 'richHtml',
      label: 'Content (above policies)',
      group: 'Content',
      control: 'richtext',
      placeholder: 'Optional content above policy links',
    },
    {
      key: 'policyLinkFontSize',
      label: 'Link Font Size (px)',
      group: 'Link Style',
      placeholder: '13',
    },
    {
      key: 'policyLinkColor',
      label: 'Link Color',
      group: 'Link Style',
      control: 'color',
      placeholder: '#333333',
    },
    {
      key: 'showRefundPolicy',
      label: 'Show Refund Policy',
      group: 'Policies',
      control: 'switch',
    },
    {
      key: 'showShippingPolicy',
      label: 'Show Shipping',
      group: 'Policies',
      control: 'switch',
    },
    {
      key: 'showPrivacyPolicy',
      label: 'Show Privacy Policy',
      group: 'Policies',
      control: 'switch',
    },
    {
      key: 'showTermsOfService',
      label: 'Show Terms of Service',
      group: 'Policies',
      control: 'switch',
    },
    {
      key: 'showSubscriptionPolicy',
      label: 'Show Cancellations',
      group: 'Policies',
      control: 'switch',
    },
    { key: 'refundLabel', label: 'Refund Policy Link', group: 'Link Text' },
    { key: 'shippingLabel', label: 'Shipping Link', group: 'Link Text' },
    { key: 'privacyLabel', label: 'Privacy Policy Link', group: 'Link Text' },
    { key: 'termsOfServiceLabel', label: 'Terms of Service Link', group: 'Link Text' },
    { key: 'subscriptionLabel', label: 'Cancellations Link', group: 'Link Text' },
  ],
}

/** Fixed sidebar / config order — children immediately follow their parent */
export const CANONICAL_SECTION_ORDER: CheckoutSectionId[] = [
  'orderSummaryMobileHeader',
  'checkoutHeader',
  'countdown',
  'expressCheckout',
  'contact',
  'delivery',
  'shippingMethod',
  'tip',
  'payment',
  'paymentCreditCard',
  'paymentBilling',
  'payButton',
  'paymentRecurring',
  'discountCode',
  'orderSummary',
  'checkoutFooter',
]

export const DEFAULT_CHECKOUT_LAYOUT_SECTIONS: CheckoutLayoutSection[] =
  CANONICAL_SECTION_ORDER.map((id) => ({
    id,
    label: SECTION_LIST_META[id].listLabel,
    // Header / countdown off by default; enable from the editor when needed.
    visible: id !== 'countdown' && id !== 'checkoutHeader',
  }))

function canonicalRank(id: CheckoutSectionId): number {
  const i = CANONICAL_SECTION_ORDER.indexOf(id)
  return i >= 0 ? i : CANONICAL_SECTION_ORDER.length
}

export function sortSectionsCanonical(
  sections: CheckoutLayoutSection[],
): CheckoutLayoutSection[] {
  return [...sections].sort((a, b) => canonicalRank(a.id) - canonicalRank(b.id))
}

/** Insert newly added sections at canonical positions without reordering existing items. */
export function insertMissingSections(
  ordered: CheckoutLayoutSection[],
  missing: CheckoutLayoutSection[],
): CheckoutLayoutSection[] {
  const result = [...ordered]
  for (const section of missing) {
    const rank = canonicalRank(section.id)
    let insertAt = result.length
    for (let i = 0; i < result.length; i++) {
      if (canonicalRank(result[i].id) > rank) {
        insertAt = i
        break
      }
    }
    result.splice(insertAt, 0, section)
  }
  return result
}

export function isSectionOrderBroken(sections: CheckoutLayoutSection[]): boolean {
  for (let i = 1; i < sections.length; i++) {
    if (canonicalRank(sections[i].id) < canonicalRank(sections[i - 1].id)) {
      return true
    }
  }
  return false
}

/**
 * Stale values persisted by older builds (when some defaults were still in
 * Chinese). When a saved value matches one of these, drop it so the current
 * (English) default applies instead.
 */
const LEGACY_CONTENT_VALUES: Partial<Record<CheckoutSectionId, SectionContent>> = {
  shippingMethod: { customEmptyText: '请添加至少一条配送方式。' },
}

export function mergeSectionContent(
  id: CheckoutSectionId,
  content?: SectionContent,
): SectionContent {
  const saved = { ...(content ?? {}) }
  const legacy = LEGACY_CONTENT_VALUES[id]
  if (legacy) {
    for (const [key, staleValue] of Object.entries(legacy)) {
      if (saved[key] === staleValue) delete saved[key]
    }
  }
  const merged = {
    ...DEFAULT_SECTION_CONTENT[id],
    ...saved,
  }
  if (id === 'tip') {
    return migrateTipContent(merged)
  }
  return merged
}

export type SectionBlockRange = {
  start: number
  end: number
  length: number
}

/** Payment drags as one block with its contiguous child rows */
export function getDragBlockRange(
  sections: CheckoutLayoutSection[],
  fromIndex: number,
): SectionBlockRange {
  const section = sections[fromIndex]
  if (!section) {
    return { start: fromIndex, end: fromIndex, length: 1 }
  }

  if (section.id === 'payment') {
    let end = fromIndex
    for (let i = fromIndex + 1; i < sections.length; i++) {
      if (SECTION_LIST_META[sections[i].id]?.parentId === 'payment') {
        end = i
      } else {
        break
      }
    }
    return { start: fromIndex, end, length: end - fromIndex + 1 }
  }

  return { start: fromIndex, end: fromIndex, length: 1 }
}

export function moveSectionsBlock(
  sections: CheckoutLayoutSection[],
  blockStart: number,
  blockEnd: number,
  insertAt: number,
): CheckoutLayoutSection[] {
  const blockLen = blockEnd - blockStart + 1
  const block = sections.slice(blockStart, blockEnd + 1)
  const rest = [
    ...sections.slice(0, blockStart),
    ...sections.slice(blockEnd + 1),
  ]
  let target = insertAt
  if (insertAt > blockStart) target = insertAt - blockLen
  target = Math.max(0, Math.min(target, rest.length))
  return [...rest.slice(0, target), ...block, ...rest.slice(target)]
}

export function isNoOpBlockMove(block: SectionBlockRange, insertAt: number): boolean {
  return insertAt === block.start || insertAt === block.end + 1
}

/** Contiguous insert range for payment child rows in the sidebar list */
export function getPaymentChildZone(
  sections: CheckoutLayoutSection[],
): { minInsert: number; maxInsert: number } | null {
  const indices: number[] = []
  for (let i = 0; i < sections.length; i++) {
    if (SECTION_LIST_META[sections[i].id]?.parentId === 'payment') {
      indices.push(i)
    }
  }
  if (indices.length === 0) return null
  return {
    minInsert: Math.min(...indices),
    maxInsert: Math.max(...indices) + 1,
  }
}

export function isPaymentChildSection(id: CheckoutSectionId): boolean {
  return SECTION_LIST_META[id]?.parentId === 'payment'
}

/** Keep payment children inside their block; keep top-level rows out of that block */
export function clampDropInsertIndex(
  sections: CheckoutLayoutSection[],
  dragId: CheckoutSectionId,
  insertAt: number,
  fromIndex?: number,
): number {
  const bounded = Math.max(0, Math.min(insertAt, sections.length))
  const zone = getPaymentChildZone(sections)
  const paymentIndex = sections.findIndex((s) => s.id === 'payment')

  if (dragId === 'payment' && fromIndex !== undefined) {
    const block = getDragBlockRange(sections, fromIndex)
    if (block.length > 1 && bounded > block.start && bounded <= block.end) {
      const rel = bounded - block.start
      return rel <= block.length / 2 ? block.start : block.end + 1
    }
    return bounded
  }

  if (!zone) return bounded

  const { minInsert, maxInsert } = zone

  if (isPaymentChildSection(dragId)) {
    return Math.max(minInsert, Math.min(bounded, maxInsert))
  }

  if (paymentIndex >= 0 && bounded > paymentIndex && bounded < maxInsert) {
    return bounded - paymentIndex <= maxInsert - bounded ? paymentIndex : maxInsert
  }

  return bounded
}

export function isSectionEffectivelyVisible(
  sections: CheckoutLayoutSection[],
  id: CheckoutSectionId,
): boolean {
  const section = sections.find((s) => s.id === id)
  if (!section || !section.visible) return false
  const parentId = SECTION_LIST_META[id]?.parentId
  if (parentId) return isSectionEffectivelyVisible(sections, parentId)
  return true
}

export function createDefaultCheckoutLayoutConfig(): CheckoutLayoutConfig {
  return {
    sections: DEFAULT_CHECKOUT_LAYOUT_SECTIONS.map((s) => ({
      ...s,
      content: { ...DEFAULT_SECTION_CONTENT[s.id] },
    })),
  }
}

const SECTION_ID_SET = new Set<string>(DEFAULT_CHECKOUT_LAYOUT_SECTIONS.map((s) => s.id))

/** Persisted layout configs may still reference retired section ids */
const LEGACY_SECTION_ID_ALIASES: Record<string, CheckoutSectionId> = {
  quickPay: 'expressCheckout',
}

export function resolveCheckoutSectionId(id: string): CheckoutSectionId | null {
  const normalized = LEGACY_SECTION_ID_ALIASES[id] ?? id
  return isCheckoutSectionId(normalized) ? normalized : null
}

export function isCheckoutSectionId(id: string): id is CheckoutSectionId {
  return SECTION_ID_SET.has(id)
}

export function normalizeCheckoutLayoutConfig(
  raw: unknown,
): CheckoutLayoutConfig | null {
  if (!raw || typeof raw !== 'object') return null
  const sections = (raw as CheckoutLayoutConfig).sections
  if (!Array.isArray(sections) || sections.length === 0) return null

  const defaults = createDefaultCheckoutLayoutConfig()
  const byId = new Map(defaults.sections.map((s) => [s.id, s]))
  const ordered: CheckoutLayoutSection[] = []

  for (const item of sections) {
    if (!item || typeof item !== 'object') continue
    const rawId = String((item as CheckoutLayoutSection).id ?? '').trim()
    const id = resolveCheckoutSectionId(rawId)
    if (!id) continue
    const def = byId.get(id)
    if (!def) continue
    ordered.push({
      id,
      label: SECTION_LIST_META[id].listLabel,
      visible: (item as CheckoutLayoutSection).visible !== false,
      content: mergeSectionContent(id, (item as CheckoutLayoutSection).content),
    })
    byId.delete(id)
  }

  const missing: CheckoutLayoutSection[] = []
  for (const id of CANONICAL_SECTION_ORDER) {
    const remaining = byId.get(id)
    if (remaining) missing.push({ ...remaining })
  }

  return { sections: insertMissingSections(ordered, missing) }
}

export function parseCheckoutLayoutFromTemplateConfig(
  templateConfig: Record<string, unknown> | undefined,
): CheckoutLayoutConfig | null {
  const layout = templateConfig?.layout
  if (!layout || typeof layout !== 'object') return null
  return normalizeCheckoutLayoutConfig(layout)
}

/** 折扣码显示位置；disabled 表示隐藏输入框。 */
export type DiscountCodeDisplay =
  | 'disabled'
  | 'orderSummary'
  | 'checkoutTop'
  | 'checkoutBottom'

export type DiscountCodeConfig = {
  display: DiscountCodeDisplay
  /** 输入框 placeholder 文案。 */
  text: string
}

const DISCOUNT_CODE_DISPLAYS: DiscountCodeDisplay[] = [
  'disabled',
  'orderSummary',
  'checkoutTop',
  'checkoutBottom',
]

export const DEFAULT_DISCOUNT_CODE_CONFIG: DiscountCodeConfig = {
  display: 'orderSummary',
  text: 'Discount code',
}

/** Placement options for the discount code section editor (excludes `disabled`). */
export const DISCOUNT_CODE_PLACEMENT_OPTIONS: SectionContentFieldOption[] = [
  { value: 'orderSummary', label: 'Order Summary' },
  { value: 'checkoutTop', label: 'Checkout Top' },
  { value: 'checkoutBottom', label: 'Checkout Bottom' },
]

function normalizeDiscountDisplay(value: unknown): DiscountCodeDisplay | null {
  const key = String(value ?? '').trim()
  return (DISCOUNT_CODE_DISPLAYS as string[]).includes(key)
    ? (key as DiscountCodeDisplay)
    : null
}

/** 从旧扁平 options（funnel.template.options）解析折扣码配置。 */
export function parseDiscountConfigFromOptions(
  options: Record<string, unknown> | undefined | null,
): DiscountCodeConfig {
  const display =
    normalizeDiscountDisplay(options?.discountCodeDisplay) ??
    DEFAULT_DISCOUNT_CODE_CONFIG.display
  const text =
    String(options?.discountCodeText ?? '').trim() ||
    DEFAULT_DISCOUNT_CODE_CONFIG.text
  return { display, text }
}

/** Map structured discount config to legacy flat template.options keys. */
export function discountConfigToLegacyOptions(
  discount: DiscountCodeConfig,
): { discountCodeDisplay: DiscountCodeDisplay; discountCodeText: string } {
  return {
    discountCodeDisplay: discount.display,
    discountCodeText: discount.text,
  }
}

/**
 * Parse discount config from a funnel template object.
 * Supports `template.discount` (new) and `template.options` (legacy).
 */
export function parseDiscountConfigFromFunnelTemplate(
  template: Record<string, unknown> | undefined | null,
): DiscountCodeConfig {
  if (!template || typeof template !== 'object') {
    return { ...DEFAULT_DISCOUNT_CODE_CONFIG }
  }

  const config =
    template.config && typeof template.config === 'object'
      ? (template.config as Record<string, unknown>)
      : undefined

  const discountObj =
    template.discount && typeof template.discount === 'object'
      ? (template.discount as Record<string, unknown>)
      : undefined

  const options =
    (template.options && typeof template.options === 'object'
      ? (template.options as Record<string, unknown>)
      : undefined) ??
    (config?.options && typeof config.options === 'object'
      ? (config.options as Record<string, unknown>)
      : undefined)

  const display =
    normalizeDiscountDisplay(discountObj?.display) ??
    normalizeDiscountDisplay(options?.discountCodeDisplay) ??
    DEFAULT_DISCOUNT_CODE_CONFIG.display

  const textRaw = discountObj?.text ?? options?.discountCodeText
  const text =
    String(textRaw ?? '').trim() || DEFAULT_DISCOUNT_CODE_CONFIG.text

  return { display, text }
}

/** Derive runtime discount config from the `discountCode` layout section. */
export function parseDiscountConfigFromSections(
  sections: CheckoutLayoutSection[],
): DiscountCodeConfig {
  const section = sections.find((s) => s.id === 'discountCode')
  if (!section) {
    return { ...DEFAULT_DISCOUNT_CODE_CONFIG, display: 'disabled' }
  }
  const content = mergeSectionContent('discountCode', section.content)
  const text =
    String(content.text ?? '').trim() || DEFAULT_DISCOUNT_CODE_CONFIG.text
  if (!section.visible) {
    return { display: 'disabled', text }
  }
  const display =
    normalizeDiscountDisplay(content.display) ?? DEFAULT_DISCOUNT_CODE_CONFIG.display
  return { display, text }
}

/** Apply legacy / template-level discount config onto the `discountCode` section. */
export function mergeDiscountIntoSections(
  sections: CheckoutLayoutSection[],
  discount: DiscountCodeConfig,
): CheckoutLayoutSection[] {
  const normalized = normalizeCheckoutLayoutConfig({ sections })
  const list = normalized?.sections ?? sections
  return list.map((section) => {
    if (section.id !== 'discountCode') return section
    const placement =
      discount.display === 'disabled' ? 'orderSummary' : discount.display
    return {
      ...section,
      visible: discount.display !== 'disabled',
      content: {
        ...mergeSectionContent('discountCode', section.content),
        display: placement,
        text: discount.text,
      },
    }
  })
}

/** Preview scroll target when the editor selects the discount code row. */
export function discountCodePreviewScrollId(
  content: SectionContent,
): CheckoutSectionId {
  const placement = normalizeDiscountDisplay(content.display) ?? 'orderSummary'
  if (placement === 'checkoutTop') return 'expressCheckout'
  if (placement === 'checkoutBottom') return 'payment'
  return 'orderSummary'
}

export function resolveEditorPreviewScrollId(
  id: CheckoutSectionId,
  sections: CheckoutLayoutSection[],
): CheckoutSectionId {
  if (id === 'discountCode') {
    const section = sections.find((s) => s.id === 'discountCode')
    return discountCodePreviewScrollId(
      mergeSectionContent('discountCode', section?.content),
    )
  }
  return sectionPreviewScrollId(id)
}
